/**
 * A record's own place on the map — the canonical form's location section
 * (#2276).
 *
 * ── IT IS NOT AN ADDRESS PAGE, AND NOT A CRM FORK ──────────────────────────
 * This mounts for ANY entity whose governed presentation resolves a map recipe.
 * `core_address` is the first, because core 2026091419 ships its recipe — but
 * nothing here names that entity, that route or that relationship, so a module
 * whose entity gains coordinates gets this section with no console change. The
 * retired CRM panel was the opposite in every respect, which is why #2276 asks
 * for "a form section, not a second address page".
 *
 * ── THE DRAFT PREVIEW, AND WHY IT CANNOT AUTOSAVE ──────────────────────────
 * It is rendered from the form's LIVE VALUES, through `EntityForm`'s `aside`
 * seam. Three properties fall out of that rather than being implemented:
 *
 *   · typing a latitude moves the pin immediately, because the values it reads
 *     are the ones the inputs are writing;
 *   · **nothing is committed**, because this component has no write door, no
 *     mutation and no effect — it renders;
 *   · **Cancel restores the persisted location**, because `onDiscard` already
 *     calls `reset(row)` and this panel reads whatever the form holds.
 *
 * A panel with its own draft state would have needed a second rollback path,
 * and the two would eventually have disagreed about what is saved.
 *
 * ── AND IT IS NEVER THE SOURCE OF TRUTH ────────────────────────────────────
 * The map does not geocode, does not offer to "locate this", and cannot be
 * dragged to set a coordinate. The fields are the truth; this draws them. A
 * draggable pin is a write, and #2276's own non-goals name "no map as authority".
 */
import { useMemo } from 'react'
import { MapLens } from '@solum/ui'
import type { MapRecipe } from '@solum/experience'
import { useGeoSettings } from './geo'
import { recordLocationView } from './locationView'

export interface RecordLocationProps {
  entity: string
  /** The record id, or '' while creating. */
  id: string
  /** The governed recipe for this entity. Absent ⇒ this section does not exist. */
  recipe: MapRecipe
  /** The form's LIVE values. Not the row: see the header. */
  values: Record<string, unknown>
  /** The canonical title, already resolved. */
  title: string
  subtitle?: string
  isNew?: boolean
  /** The caller may not write this record; the section is then purely a reading. */
  readOnly?: boolean
  /** A refusal or failure the host already worded. */
  problem?: string
}

/**
 * The tile seam, from the deployment's OWN setting.
 *
 * `crm/geo.ts` is the governed home for this (crm `2026080402`, #524): the
 * provider moved out of `localStorage` into `core.setting` precisely so it would
 * be admin-gated, audited and maskable, and there is exactly one keyless
 * provider because an embed carries its key in the page and a governed secret
 * cannot. Reading that setting rather than declaring a second one is the
 * difference between a seam and a fork.
 *
 * `none` returns null and the lens draws its explicit "no map provider" state,
 * with the record's own location still stated in words. That is the
 * offline/refused case, said out loud.
 */
function useTiles() {
  const { mapProvider } = useGeoSettings()
  return useMemo(() => (mapProvider === 'osm'
    ? { urlTemplate: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap', maxZoom: 19 }
    : null), [mapProvider])
}

export function RecordLocation({
  entity, id, recipe, values, title, subtitle, isNew = false, readOnly = false, problem,
}: RecordLocationProps) {
  const tiles = useTiles()
  const lat = values[recipe.lat]
  const lng = values[recipe.lng]

  const view = useMemo(
    () => recordLocationView({ entity, id, recipe, values, title, subtitle, isNew }),
    // The COORDINATE values are the dependency, deliberately: re-deriving the
    // pin because an unrelated field changed would re-fit the map under somebody
    // mid-edit. `values` itself is a new object on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity, id, recipe.lat, recipe.lng, recipe.title, recipe.subtitle, title, subtitle, isNew, lat, lng],
  )

  return (
    <MapLens
      presentation="record"
      /* The form sits directly under the record page's own h1 and its sections
         are fieldsets rather than headings, so this lens's title is the h2.
         Left at the workspace default it would skip a level. */
      headingLevel={2}
      /* The heading says which of the two states this is. On a record being
         edited the pin is a PREVIEW of unsaved values, and calling it
         "Location" would let somebody read an uncommitted coordinate as the
         stored one. */
      heading={readOnly ? 'Location' : 'Location preview'}
      pins={view.pins}
      unplaceable={view.unplaceable}
      tiles={tiles}
      problem={problem}
      height={260}
      testId="record-location"
    />
  )
}
